import React, { useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import './ViewportOverlay.css';
import {
  computeEquineOverlayPosition,
  type EquineOverlayGeometry,
} from './equineOverlayPosition';

// The overlay-top and overlay-bottom classes are explicitly needed to offset
// the overlays (i.e. via absolute positioning) such the ViewportActionCorners
// have space for its child components.
// ToDo: offset the ViewportOverlay automatically via css to account for the
// space needed for ViewportActionCorners.
const classes = {
  topLeft: 'overlay-top left-viewport',
  topRight: 'overlay-top right-viewport-scrollbar',
  bottomRight: 'overlay-bottom right-viewport-scrollbar',
  bottomLeft: 'overlay-bottom left-viewport',
};

export type ViewportOverlayProps = {
  topLeft: React.ReactNode;
  topRight: React.ReactNode;
  bottomRight: React.ReactNode;
  bottomLeft: React.ReactNode;
  color?: string;
  topLeftGeometry?: EquineOverlayGeometry;
};

const ViewportOverlay = ({
  topLeft,
  topRight,
  bottomRight,
  bottomLeft,
  color = 'text-primary-light',
  topLeftGeometry,
}: ViewportOverlayProps) => {
  const overlay = 'absolute pointer-events-none viewport-overlay';
  const topLeftRef = useRef<HTMLDivElement>(null);
  const [topLeftPosition, setTopLeftPosition] = useState({ left: 0, top: 28 });

  useLayoutEffect(() => {
    const node = topLeftRef.current;
    if (!node || !topLeftGeometry) return;

    const update = () => {
      const rect = node.getBoundingClientRect();
      setTopLeftPosition(
        computeEquineOverlayPosition({
          ...topLeftGeometry,
          overlay: { width: rect.width, height: rect.height },
        })
      );
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [topLeftGeometry]);

  const topLeftStyle = {
    '--equine-overlay-top-left': `${topLeftPosition.top}px`,
    '--equine-overlay-left': `${topLeftPosition.left}px`,
  } as React.CSSProperties;

  return (
    <div
      className={classnames(
        color ? color : 'text-aqua-pale',
        'text-[13px]',
        'leading-5',
        'overlay-text'
      )}
    >
      <div
        ref={topLeftRef}
        data-cy={'viewport-overlay-top-left'}
        className={classnames(overlay, classes.topLeft)}
        style={topLeftStyle}
      >
        {topLeft}
      </div>
      <div
        data-cy={'viewport-overlay-top-right'}
        className={classnames(overlay, classes.topRight)}
        style={{ transform: 'translateX(-8px)' }} // shift right side overlays by 4px for better alignment with ViewportActionCorners' icons
      >
        {topRight}
      </div>
      <div
        data-cy={'viewport-overlay-bottom-right'}
        className={classnames(overlay, classes.bottomRight)}
        style={{ transform: 'translateX(-8px)' }} // shift right side overlays by 4px for better alignment with ViewportActionCorners' icons
      >
        {bottomRight}
      </div>
      <div
        data-cy={'viewport-overlay-bottom-left'}
        className={classnames(overlay, classes.bottomLeft)}
      >
        {bottomLeft}
      </div>
    </div>
  );
};

export default ViewportOverlay;
